import React from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useSearchParams} from 'react-router-dom';
import {DateRange} from 'rsuite/DateRangePicker';
import {t} from '@lingui/core/macro';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import FormGroup from 'shared/ui/FormGroup/FormGroup';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import Select from 'shared/ui/Select/Select';
import DateRangePickerControlled from 'shared/ui/Date/DateRangePickerControlled/DateRangePickerControlled';
import OrderByControlled from 'shared/ui/OrderBy/OrderByControlled';
import IOrderBy from 'shared/model/IOrderBy';
import {IPortMonitorListItem} from 'features/port/model/PortMonitor/IPortMonitor';
import {getPortMonitorName} from 'features/port/lib/portMonitorHelper';
import {ALL_FILTER_VALUE, booleanToString, stringsToDateRange} from 'shared/lib/filterHelper';
import {useSort} from 'shared/lib/hooks/useSort';
import {getPortIncidentFilters, PORT_INCIDENTS_ORDER_FIELDS} from 'features/port/lib/portFilters';

interface IPortIncidentsFiltersFormProps {
    monitors: IPortMonitorListItem[],
    onSubmit: SubmitHandler<IPortIncidentFiltersForm>,
    button: React.ReactElement,
}

export interface IPortIncidentFiltersForm {
    portMonitor?: string,
    resolved?: string,
    created_at?: DateRange,
    resolved_at?: DateRange,
    orderBy?: IOrderBy,
}

const SEARCHABLE_MONITORS_COUNT = 5;

const PortIncidentsFiltersForm: React.FC<IPortIncidentsFiltersFormProps> = ({monitors, onSubmit, button}) => {
    const [searchParams] = useSearchParams();
    const filters = getPortIncidentFilters(searchParams);
    const [sort] = useSort({sortFields: PORT_INCIDENTS_ORDER_FIELDS});

    const monitorOptions = [
        {value: ALL_FILTER_VALUE, label: t`Все мониторы`},
        ...monitors.map(monitor => ({value: monitor.uuid, label: getPortMonitorName(monitor)})),
    ];

    const statusOptions = [
        {value: ALL_FILTER_VALUE, label: t`Любой статус`},
        {value: 'false', label: t`Открыт`},
        {value: 'true', label: t`Решён`},
    ];

    const sortOptions = [
        {value: 'createdAt', label: t`Начало`},
        {value: 'resolvedAt', label: t`Окончание`},
        {value: 'resolved', label: t`Статус`},
    ];

    const {handleSubmit, control} = useForm<IPortIncidentFiltersForm>({
        mode: 'onSubmit',
        defaultValues: {
            portMonitor: filters['portMonitor.uuid'] ?? ALL_FILTER_VALUE,
            resolved: booleanToString(filters.resolved) ?? ALL_FILTER_VALUE,
            created_at: stringsToDateRange(filters['created_at[after]'], filters['created_at[before]']),
            resolved_at: stringsToDateRange(filters['resolved_at[after]'], filters['resolved_at[before]']),
            orderBy: sort,
        },
    });

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBodyWrapper>
                <FormGroupWithError label={t`Монитор`}>
                    <Select
                        control={control}
                        name="portMonitor"
                        data={monitorOptions}
                        searchable={monitors.length > SEARCHABLE_MONITORS_COUNT}
                    />
                </FormGroupWithError>

                <FormGroupWithError label={t`Статус`}>
                    <Select control={control} name="resolved" data={statusOptions} searchable={false}/>
                </FormGroupWithError>

                <FormGroupWithError label={t`Период открытия инцидента`}>
                    <DateRangePickerControlled control={control} name="created_at" cleanable={true}/>
                </FormGroupWithError>

                <FormGroupWithError label={t`Период решения инцидента`}>
                    <DateRangePickerControlled control={control} name="resolved_at" cleanable={true}/>
                </FormGroupWithError>

                <FormGroupWithError label={t`Сортировка`}>
                    <OrderByControlled control={control} name="orderBy" defaultValue={sort} data={sortOptions}/>
                </FormGroupWithError>
            </ModalBodyWrapper>
            <FormGroup>
                {button}
            </FormGroup>
        </form>
    );
};

export default PortIncidentsFiltersForm;
